import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API } from '../../services/api';

const api = new API();

export const fetchTyreAwards = createAsyncThunk(
  'tyreAwards/get',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId } = data;
      const response = await api.getTyreAwards(itemOwnerId);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/get', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const fetchTyreAwardMagazines = createAsyncThunk(
  'tyreAwards/getMagazines',
  async (data, thunkAPI) => {
    try {
      const response = await api.getTyreAwardMagazines();
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/get', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const fetchTyreAward = createAsyncThunk(
  'tyreAwards/getOne',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, awardId } = data;
      const response = await api.getTyreAward(itemOwnerId, awardId);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/getOne', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const addTyreAward = createAsyncThunk(
  'tyreAwards/add',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId, award } = data;
      const response = await api.addTyreAward(itemOwnerId, itemFamilyId, award);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/add', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const updateTyreAward = createAsyncThunk(
  'tyreAwards/update',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, itemFamilyId, award } = data;
      const response = await api.updateTyreAward(itemOwnerId, itemFamilyId, award);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/update', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const updateTyreAwardTranslation = createAsyncThunk(
  'tyreAwards/updateTranslation',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, award } = data;
      const response = await api.updateTyreAwardTranslation(itemOwnerId, award.id, award);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/updateTranslation', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const updateTyreAwardOrder = createAsyncThunk(
  'tyreAwards/updateTyreAwardOrder',
  async (data, thunkAPI) => {
    try {
      const { itemOwnerId, awards } = data;
      const response = await api.updateTyreAwardOrder(itemOwnerId, awards);
      if (response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch (e) {
      // System error or status code >= 500
      console.error('ERROR: tyreAwards/updateTyreAwardOrder', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);
// action.meta.arg.id

const initialState = {
  awards: [],
  countries: [],
  seasons: [],
  testSeasons: [],
  magazines: [],
  fetchStatus: 'idle',
  fetchError: null,
  fetchSingleStatus: 'idle',
  fetchSingleError: null,
  saveStatus: 'idle',
  saveError: null,
  saveTranslationStatus: 'idle',
  saveTranslationError: null,
  saveOrderStatus: 'idle',
  saveOrderError: null
};

export const tyreAwardsSlice = createSlice({
  name: 'tyreAwards',
  initialState,
  reducers: {
    clearSingleRequestState: (state, action) => {
      state.fetchSingleStatus = 'idle';
      state.fetchSingleError = null;
      state.saveTranslationStatus = 'idle';
      state.saveTranslationError = null;
      return state;
    },
    setFetchSingleState: (state, action) => {
      state.fetchSingleStatus = action.payload.status;
      state.fetchSingleError = action.payload.error;
      return state;
    }
  },
  extraReducers: {
    // Add reducers for additional action types here, and handle loading state as needed
    [fetchTyreAwards.fulfilled]: (state, action) => {
      state.fetchStatus = 'succeeded';
      state.fetchError = null;
      state.awards = action.payload.awards.map(a => ({ ...a, languages: {} })) || [];
      state.countries = action.payload.countries || [];
      state.testSeasons = action.payload.testSeasons || [];
      state.seasons = action.payload.seasons || [];
      state.years = action.payload.years || [];
    },
    [fetchTyreAwards.pending]: (state, action) => {
      state.fetchStatus = 'loading';
      state.fetchError = null;
    },
    [fetchTyreAwards.rejected]: (state, action) => {
      state.fetchStatus = 'failed';

      let errorMessage = '';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.fetchError = {
        message: errorMessage
      };
    },
    [fetchTyreAwardMagazines.fulfilled]: (state, action) => {
      state.magazines = action.payload || [];
    },
    [fetchTyreAward.fulfilled]: (state, action) => {
      state.fetchSingleStatus = 'succeeded';
      state.fetchSingleError = null;
      const newAward = action.payload;
      // add property singleFetched so we know we have all languages
      state.awards = state.awards.map(award => award.id !== newAward.id ? award : {
        ...newAward,
        singleFetched: true
      })
    },
    [fetchTyreAward.pending]: (state, action) => {
      state.fetchSingleStatus = 'loading';
      state.fetchSingleError = null;
    },
    [fetchTyreAward.rejected]: (state, action) => {
      state.fetchSingleStatus = 'failed';

      let errorMessage = '';
      console.log(action.payload)
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.fetchSingleError = {
        message: errorMessage
      };
    },
    [addTyreAward.fulfilled]: (state, action) => {
      state.saveStatus = 'succeeded';
      state.saveError = null;
      const newAward = action.payload;
      state.awards = [...state.awards, { ...newAward, singleFetched: true }]
    },
    [addTyreAward.pending]: (state, action) => {
      state.saveStatus = 'loading';
      state.saveError = null;
    },
    [addTyreAward.rejected]: (state, action) => {
      state.saveStatus = 'failed';

      let errorMessage = '';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.saveError = {
        message: errorMessage
      };
    },
    [updateTyreAward.fulfilled]: (state, action) => {
      state.saveStatus = 'succeeded';
      state.saveError = null;
      const updatedAward = action.payload;

      state.awards = state.awards.map(award => award.id !== updatedAward.id ? award : {
        ...updatedAward,
        singleFetched: true
      })
    },
    [updateTyreAward.pending]: (state, action) => {
      state.saveStatus = 'loading';
      state.saveError = null;
    },
    [updateTyreAward.rejected]: (state, action) => {
      state.saveStatus = 'failed';

      let errorMessage = '';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.saveError = {
        message: errorMessage
      };
    },
    [updateTyreAwardTranslation.fulfilled]: (state, action) => {
      state.saveTranslationStatus = 'succeeded';
      state.saveTranslationError = null;
      const updated = action.meta.arg.award;

      state.awards = state.awards.map(award => award.id !== updated.id ? award : {
        ...award,
        translations: action.payload.translations
      })
    },
    [updateTyreAwardTranslation.pending]: (state, action) => {
      state.saveTranslationStatus = 'loading';
      state.saveTranslationError = null;
    },
    [updateTyreAwardTranslation.rejected]: (state, action) => {
      state.saveTranslationStatus = 'failed';

      let errorMessage = '';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.saveTranslationError = {
        message: errorMessage
      };
    },
    [updateTyreAwardOrder.fulfilled]: (state, action) => {
      state.saveOrderStatus = 'succeeded';
      state.saveOrderError = null;
      const updatedAwards = action.payload.awards;

      const existingAwards = state.awards.filter(award => !updatedAwards.find(a => a.id === award.id));
      state.awards = existingAwards.concat(updatedAwards);
    },
    [updateTyreAwardOrder.pending]: (state, action) => {
      state.saveOrderStatus = 'loading';
      state.saveOrderError = null;
    },
    [updateTyreAwardOrder.rejected]: (state, action) => {
      state.saveOrderStatus = 'failed';

      let errorMessage = '';
      if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload.messages) {
        errorMessage = action.payload.messages.join(', ');
      }

      state.saveOrderError = {
        message: errorMessage
      };
    },
    //TODO: should reset if owner changes
  }
});

export const selectTyreAwards = state => state.tyreAwards;

export const { clearSingleRequestState, setFetchSingleState } = tyreAwardsSlice.actions;

export default tyreAwardsSlice.reducer;
